class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    // Check if we need to preserve color filtering before updating content
    const urlParams = new URLSearchParams(window.location.search);
    const hasColorFilter = urlParams.has('color');
    
    if (hasColorFilter) {
      console.log('🔧 Color filter detected, will apply immediately after content update');
    }

    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });

    // Re-apply color filtering immediately if there's a color parameter
    if (hasColorFilter) {
      console.log('🔧 Re-applying color filtering immediately after AJAX update');
      FacetFiltersForm.reapplyColorFiltering();
    }
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    // Restore color checkbox states after AJAX update
    FacetFiltersForm.restoreColorCheckboxes();

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  static reapplyColorFiltering() {
    console.log('🔧 reapplyColorFiltering called');
    
    // Check if there are color or category parameters in the URL
    const urlParams = new URLSearchParams(window.location.search);
    const colorParam = urlParams.get('color');
    const categoryParam = urlParams.get('category');
    
    if (!colorParam && !categoryParam) {
      console.log('🔧 No color or category parameters found, skipping filtering');
      return;
    }
    
    console.log('🔧 Found parameters - Color:', colorParam, 'Category:', categoryParam);
    
    const productGrid = document.getElementById('product-grid');
    if (!productGrid) {
      console.log('🔧 Product grid not found');
      return;
    }
    
    // Split multiple values (comma-separated)
    const colors = colorParam ? colorParam.split(',').map(color => color.trim().toLowerCase()) : [];
    const categories = categoryParam ? categoryParam.split(',').map(category => category.trim().toLowerCase()) : [];
    
    console.log('🔧 Colors to filter:', colors);
    console.log('🔧 Categories to filter:', categories);
    
    // If category filters are active, we need to handle collection-based filtering
    if (categories.length > 0) {
      console.log('🔧 Category filters detected, triggering data-type based filtering');
      
      // Use the data-type based filtering function from facets.liquid
      if (typeof window.filterByCategories === 'function') {
        window.filterByCategories(categories, colors);
      } else {
        console.log('🔧 Category filtering function not available, falling back to simple filtering');
        this.simpleFilterProducts(colors, categories);
      }
    } else {
      // Only color filters, use simple title-based filtering
      this.simpleFilterProducts(colors, []);
    }
  }

  static simpleFilterProducts(colors, categories) {
    const productGrid = document.getElementById('product-grid');
    const allItems = productGrid.querySelectorAll('li.grid__item--product, li.grid__item--variant');
    
    // Track seen product IDs to avoid duplicates
    const seenProductIds = new Set();
    
    let visibleCount = 0;
    allItems.forEach(item => {
      const productId = item.getAttribute('data-product-id');
      const titleElement = item.querySelector('.card__heading a, .card__heading, .card-product__title a, .card-product__title, .card__content h3 a, .card__content h3, h3 a, h3');
      
      if (titleElement) {
        const productTitle = (titleElement.textContent || titleElement.innerText || '').toLowerCase().trim();
        
        let matchesFilters = true;
        
        // Check color filters (if any are selected)
        if (colors.length > 0) {
          const matchesAnyColor = colors.some(color => productTitle.includes(color));
          if (!matchesAnyColor) {
            matchesFilters = false;
          }
        }
        
        // Check category filters (if any are selected) - use data-type attribute
        if (categories.length > 0 && matchesFilters) {
          const dataType = item.getAttribute('data-type');
          const matchesAnyCategory = categories.some(category => 
            dataType && dataType.toLowerCase() === category.toLowerCase()
          );
          if (!matchesAnyCategory) {
            matchesFilters = false;
          }
        }
        
        // Check for duplicates - if we've already shown this product, hide this instance
        const isDuplicate = productId && seenProductIds.has(productId);
        
        // Show or hide based on match and duplicate check
        if (matchesFilters && !isDuplicate) {
          item.style.display = '';
          visibleCount++;
          if (productId) {
            seenProductIds.add(productId);
          }
        } else {
          item.style.display = 'none';
        }
      } else {
        item.style.display = 'none';
      }
    });
    
    console.log('🔧 Applied simple filtering - Colors:', colors, 'Categories:', categories, 'Visible items:', visibleCount, 'Unique products:', seenProductIds.size);
    
    // Add filter_active class to body
    document.body.classList.add('filter_active');
    
    // Update product counts to reflect filtered results
    FacetFiltersForm.updateProductCounts(visibleCount);
    
    // Re-initialize pagination after filtering
    if (typeof window.initClientSidePagination === 'function') {
      setTimeout(() => {
        window.initClientSidePagination();
      }, 10);
    }
  }

  static restoreColorCheckboxes() {
    console.log('🔧 restoreColorCheckboxes called');
    
    // Check if there are color or category parameters in the URL
    const urlParams = new URLSearchParams(window.location.search);
    const colorParam = urlParams.get('color');
    const categoryParam = urlParams.get('category');
    
    if (!colorParam && !categoryParam) {
      console.log('🔧 No color or category parameters found, skipping checkbox restoration');
      return;
    }
    
    // Handle color checkboxes
    if (colorParam) {
      const colors = colorParam.split(',').map(color => color.trim().toLowerCase());
      console.log('🔧 Restoring checkboxes for colors:', colors);
      
      colors.forEach(color => {
        const checkboxes = document.querySelectorAll('.color-filter-checkbox[data-color-value="' + color + '"]');
        checkboxes.forEach(checkbox => {
          checkbox.checked = true;
          console.log('🔧 Restored color checkbox:', checkbox.id, 'for color:', color);
        });
      });
    }
    
    // Handle category checkboxes
    if (categoryParam) {
      const categories = categoryParam.split(',').map(category => category.trim().toLowerCase());
      console.log('🔧 Restoring checkboxes for categories:', categories);
      
      categories.forEach(category => {
        const checkboxes = document.querySelectorAll('.category-filter-checkbox[data-category-value="' + category + '"]');
        checkboxes.forEach(checkbox => {
          checkbox.checked = true;
          console.log('🔧 Restored category checkbox:', checkbox.id, 'for category:', category);
        });
      });
    }
    
    // Add filter_active class to body since filters are active
    document.body.classList.add('filter_active');
  }

  static updateProductCounts(visibleCount) {
    console.log('🔧 updateProductCounts called with count:', visibleCount);
    
    // Update all product count elements
    const countElements = [
      document.getElementById('ProductCount'),
      document.getElementById('ProductCountDesktop'),
      document.querySelector('.mobile-facets__open-label.button-label.medium-hide.large-up-hide'),
      document.querySelector('.mobile-facets__open-label.button-label.small-hide'),
      document.querySelector('.mobile-facets__heading.medium-hide.large-up-hide'),
      document.querySelector('.mobile-facets__heading.small-hide')
    ];
    
    countElements.forEach(element => {
      if (element) {
        const currentText = element.textContent || element.innerText || '';
        console.log('🔧 Updating element:', element.tagName, element.className, 'from:', currentText);
        
        if (element.id === 'ProductCount' || element.id === 'ProductCountDesktop') {
          // For main product count elements
          element.textContent = `${visibleCount} products`;
        } else if (element.classList.contains('mobile-facets__open-label')) {
          // For mobile filter button labels
          element.textContent = `Filter (${visibleCount})`;
        } else if (element.classList.contains('mobile-facets__heading')) {
          // For mobile facets headings
          if (currentText.includes('Filter & Sort')) {
            element.innerHTML = `Filter &amp; Sort (${visibleCount})`;
          } else {
            element.textContent = `Filter (${visibleCount})`;
          }
        }
        
        console.log('🔧 Updated to:', element.textContent || element.innerHTML);
      }
    });
  }

  static updateProductCountsFromGrid() {
    console.log('🔧 updateProductCountsFromGrid called');
    
    const productGrid = document.getElementById('product-grid');
    if (!productGrid) {
      console.log('🔧 Product grid not found');
      return;
    }
    
    // Count visible items
    const allItems = productGrid.querySelectorAll('li.grid__item--product, li.grid__item--variant');
    const visibleItems = Array.from(allItems).filter(item => {
      const style = window.getComputedStyle(item);
      return style.display !== 'none';
    });
    
    const visibleCount = visibleItems.length;
    console.log('🔧 Found', visibleCount, 'visible items out of', allItems.length, 'total items');
    
    FacetFiltersForm.updateProductCounts(visibleCount);
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    const formParams = new URLSearchParams(formData);
    
    // Preserve existing URL parameters that aren't in the form
    const currentParams = new URLSearchParams(window.location.search);
    
    // List of parameters to preserve from the current URL (in order of priority)
    const preserveParams = ['color', 'category', 'query', 'variant', 'mode', 'option'];
    
    console.log('🔧 createSearchParams - Current URL params:', currentParams.toString());
    console.log('🔧 createSearchParams - Form params:', formParams.toString());
    
    // Start with a new URLSearchParams and add preserved parameters first
    const finalParams = new URLSearchParams();
    
    // Add preserved parameters first (in the specified order)
    preserveParams.forEach(param => {
      if (currentParams.has(param)) {
        finalParams.set(param, currentParams.get(param));
        console.log('🔧 createSearchParams - Preserved param:', param, '=', currentParams.get(param));
      }
    });
    
    // Then add form parameters (these will override preserved params if there's a conflict)
    formParams.forEach((value, key) => {
      finalParams.set(key, value);
    });
    
    console.log('🔧 createSearchParams - Final params:', finalParams.toString());
    return finalParams.toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
